/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    MouseEventHandler,
    TextareaHTMLAttributes,
    useEffect,
    useRef,
    useState
} from 'react';
import {generateComponentId} from '../util/componentUtil';
import {enterPressed} from '../util/controlUtil';
import {ErrorType, OrnamentShape} from '../util/interfaces';
import DvrdInput from "./dvrdInput";
import NumberString from "../util/numberString";

export interface InputControllerProps {
    onChange: (value: string, evt: React.ChangeEvent) => void;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onKeyUp?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    onClearInput?: MouseEventHandler;
    value: string | number | NumberString;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: InputHTMLAttributes<any> & TextareaHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    fullWidth?: boolean;
    placeholder?: string;
    type?: string;
    max?: number;
    min?: number;
    step?: number;
    area?: boolean;
    noResize?: boolean;
    unControlled?: boolean;
}

export default function DvrdInputController(props: InputControllerProps) {
    const {
            disabled, onChange, onEnter, onKeyDown, inputProps, placeholder, type, max, min, step, onFocus, onBlur,
            autoFocus, error, label, ornaments, className, inputClassName, labelClassName, ornamentClassName, fullWidth,
            errorClassName, autoSelect, area, noResize, onClearInput, unControlled, onKeyUp
        } = props,
        componentId = useRef(generateComponentId(props.id)),
        [value, setValue] = useState(props.value);

    function _onChange(evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (disabled) return;
        const {value} = evt.target;
        setValue(value);
        onChange(value, evt);
    }

    function _onKeyDown(evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (disabled) return;
        if (onEnter && enterPressed(evt)) onEnter(evt);
        else if (onKeyDown) onKeyDown(evt);
    }

    function getInputProps(): InputHTMLAttributes<any> | undefined {
        if (!inputProps && !placeholder && !type) return undefined;
        const props: InputHTMLAttributes<any> = inputProps ?? {};
        if (placeholder !== undefined) props.placeholder = placeholder;
        if (type !== undefined) props.type = type;
        if (max !== undefined) props.max = max;
        if (min !== undefined) props.min = min;
        if (step !== undefined) props.step = step;
        return props;
    }

    useEffect(() => {
        if(!unControlled) setValue(props.value);
    }, [props.value])

    return (
        <DvrdInput onChange={_onChange} onFocus={onFocus} onKeyDown={_onKeyDown} value={value}
                   disabled={disabled} autoFocus={autoFocus} error={error} label={label} ornaments={ornaments}
                   inputProps={getInputProps()} className={className} inputClassName={inputClassName}
                   labelClassName={labelClassName} ornamentClassName={ornamentClassName} fullWidth={fullWidth}
                   errorClassName={errorClassName} id={componentId.current} onBlur={onBlur} autoSelect={autoSelect}
                   area={area} noResize={noResize} onClearInput={onClearInput} onKeyUp={onKeyUp}/>
    );
}